import { RolodexEntryType } from 'types'
import type { z } from 'zod'

import type { DocumentOcrJSON } from './ocrViewer'
import {
  SystemDocumentEntrySchema,
  SystemDocumentJSONSchema,
} from './systemDocumentEntrySchemas'
import type { SystemDocumentPageEntry } from './systemDocumentPageSchemas'

export type SystemDocumentJSON = z.infer<typeof SystemDocumentJSONSchema>
export type SystemDocumentEntry = z.infer<typeof SystemDocumentEntrySchema>

export const DOCUMENT_ID_FIELD = 'document_id'
export const PAGE_ID_FIELD = 'page_id'
export const PAGE_NUM_FIELD = 'page_num'
export const PIPELINE_CONFIG_HASH_FIELD = 'pipeline_config_hash'
export const CANTILEVER_DOCUMENT_TAG = 'cantilever-document'
export const CANTILEVER_DOCUMENT_PAGE_TAG = 'cantilever-document-page'
export const SYSTEM_DOCUMENT_TAG = 'system-document'

export type DocumentOcrEntry = {
  entry: {
    id: string
    type: RolodexEntryType.DOCUMENT_OCR
    data: DocumentOcrJSON
    tags: {
      site: string
      'system-document': string
    }
  }
}

export function getSystemDocumentBqId(
  entry: SystemDocumentEntry
): string | undefined {
  const fromField = entry.entry.data.document_id
  if (typeof fromField === 'string' && fromField.length > 0) {
    return fromField
  }
  return undefined
}

/**
 * Display title for a system document. `title` alone is unreliable — some
 * onboardings leave it empty while `document_title` or `drawing_number` is
 * populated, so every title surface must use the same fallback chain.
 */
export function getSystemDocumentDisplayTitle(
  entry: SystemDocumentEntry
): string | undefined {
  return (
    entry.entry.data.document_title?.trim() ||
    entry.entry.data.drawing_number?.trim() ||
    entry.entry.data.title?.trim() ||
    undefined
  )
}

/** Drawing number for a system document, when populated. */
export function getSystemDocumentDrawingNumber(
  entry: SystemDocumentEntry
): string | undefined {
  return entry.entry.data.drawing_number?.trim() || undefined
}

export function getIntraDocSearchDocumentId(
  entry: SystemDocumentEntry
): string {
  const sourceDocumentId = entry.entry.data.source_document_id
  return typeof sourceDocumentId === 'string' && sourceDocumentId.length > 0
    ? sourceDocumentId
    : entry.entry.id
}

export function getCantileverDocumentEntryId(
  entry: SystemDocumentEntry
): string | undefined {
  const tag = entry.entry.tags[CANTILEVER_DOCUMENT_TAG]
  if (typeof tag === 'string' && tag.length > 0) {
    return tag
  }
  return undefined
}

export function getCantileverDocumentPageId(
  entry: SystemDocumentPageEntry
): string | undefined {
  const tag = entry.entry.tags?.[CANTILEVER_DOCUMENT_PAGE_TAG]
  if (typeof tag === 'string' && tag.length > 0) {
    return tag
  }
  return undefined
}
